import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "fixanor.language";
const LANGUAGE_QUERY_PARAM = "lang";

export const LANGUAGE_OPTIONS = [
  { code: "hu", shortLabel: "HU", label: "Magyar" },
  { code: "ro", shortLabel: "RO", label: "Română" },
  { code: "en", shortLabel: "EN", label: "English" },
  { code: "de", shortLabel: "DE", label: "Deutsch" },
];

const languageCodes = LANGUAGE_OPTIONS.map((language) => language.code);
const isSupportedLanguage = (language) => languageCodes.includes(language);

const translations = {
  hu: {
    common: {
      email: "Email",
      phone: "Telefon",
      privacyPolicy: "Adatvédelmi szabályzat",
      cookieSettings: "Süti beállítások",
    },
    language: {
      label: "Nyelv",
      openMenu: "Nyelv kiválasztása",
      select: "Váltás erre a nyelvre:",
    },
    theme: {
      toggleToDark: "Sötét mód bekapcsolása",
      toggleToLight: "Világos mód bekapcsolása",
    },
    seo: {
      defaultTitle: "Fixanor Software Solutions",
      defaultDescription: "Professzionális webfejlesztés és szoftvermegoldások Nagyváradról.",
      homeTitle: "Kezdőlap",
      homeDescription:
        "Fixanor Software Solutions - professzionális webfejlesztés, egyedi szoftverek és digitális megoldások, hogy vállalkozása online növekedhessen.",
      contactTitle: "Kapcsolat",
      contactDescription:
        "Lépjen kapcsolatba a Fixanor Software Solutions csapatával webfejlesztéshez és egyedi szoftvermegoldásokhoz.",
      privacyTitle: "Adatvédelmi szabályzat",
      privacyDescription:
        "Ismerje meg, hogyan gyűjti, kezeli és védi a Fixanor Software Solutions az adatait.",
      impressumTitle: "Impresszum",
      impressumDescription:
        "A Fixanor Software Solutions S.R.L. hivatalos cégadatai és jogi tájékoztatója.",
      notFoundTitle: "Az oldal nem található",
      notFoundDescription:
        "A keresett oldal nem található. Fedezze fel a Fixanor Software Solutions szolgáltatásait és szakértelmét.",
    },
    hero: {
      titleLine1: "Építsük a szoftver",
      titleLine2: "jövőjét",
      cta: "Kapcsolat",
      imageAlt: "Szoftverfejlesztő",
    },
    about: {
      title: "A Fixanorról",
      intro: {
        before:
          "egy szoftverfejlesztő vállalkozás Nagyváradon, Romániában. Modern, gyors és megbízható",
        websites: "weboldalak",
        between: "és",
        webApplications: "webalkalmazások",
        after: " fejlesztésére specializálódtunk, ügyfeleink egyedi igényeire szabva.",
      },
      philosophyPrefix: "Filozófiánk egyszerű:",
      philosophyQuote: "Briliáns megoldások a kihívásokra.",
      approach:
        "A kreativitást, a technikai szakértelmet és az ügyfélközpontú szemléletet egyesítjük, hogy ötleteiből skálázható digitális megoldások szülessenek.",
      values: [
        {
          title: "Innováció",
          description: "Modern technológiákkal készítünk élvonalbeli digitális élményeket.",
        },
        {
          title: "Partnerség",
          description: "Átlátható együttműködésben és hosszú távú kapcsolatokban hiszünk.",
        },
        {
          title: "Egyszerűség",
          description: "Felhasználóbarát, hatékony és elegáns megoldásokat építünk minden projekthez.",
        },
      ],
      closing:
        "Legyen szó céges weboldalról, webáruházról vagy egyedi webalkalmazásról, a Fixanor segít sikeresnek lenni a digitális világban.",
    },
    carousel: {
      title: "Technológiai eszköztárunk",
      logoAlt: "Technológiai logó",
    },
    contact: {
      title: "Kapcsolat",
      placeholders: {
        subject: "Tárgy",
        name: "Az Ön neve",
        email: "E-mail címe",
        message: "Üzenete",
      },
      consentBefore: "Hozzájárulok, hogy a Fixanor az adataimat kezelje az",
      consentAfter: "szerint.",
      functionalCookiesMessage: "Az űrlap elküldéséhez engedélyezze a funkcionális sütiket a",
      functionalCookiesSuffix: "menüben.",
      turnstileNotConfiguredNotice: "A Turnstile nincs beállítva ebben a környezetben.",
      send: "Küldés",
      alerts: {
        rateLimit: "Kérjük, várjon legalább 2 percet egy újabb üzenet elküldése előtt.",
        functionalCookies: "Kérjük, engedélyezze a funkcionális sütiket a süti beállításokban.",
        turnstileNotConfigured: "A Turnstile nincs beállítva. Kérjük, lépjen kapcsolatba az ügyfélszolgálattal.",
        captcha: "Kérjük, igazolja, hogy nem robot.",
        privacy: "A folytatáshoz fogadja el az adatvédelmi szabályzatot.",
        success: "Üzenete sikeresen elküldve!",
        backendNotConfigured: "A backend URL nincs beállítva.",
        errorPrefix: "Hiba: ",
        networkErrorPrefix: "Hálózati hiba: ",
      },
    },
    footer: {
      rights: "Minden jog fenntartva.",
      impressum: "Impresszum",
    },
    cookie: {
      settings: "Süti beállítások",
      bannerAria: "Süti tájékoztató",
      bannerTextBefore:
        "Ez a weboldal sütiket használ az alap működéshez és a jobb felhasználói élményhez. A nem elengedhetetlen sütiket, például a funkcionális, analitikai és marketing sütiket, csak az Ön hozzájárulásával használjuk. Tekintse meg az",
      bannerTextAfter: ".",
      decline: "Elutasítás",
      manage: "Beállítások kezelése",
      acceptAll: "Összes elfogadása",
      preferencesAria: "Süti beállítások",
      preferencesTitle: "Süti beállítások",
      close: "Bezárás",
      preferencesDescription: "Válassza ki, mely kategóriákat engedélyezi. Ezt bármikor módosíthatja.",
      cancel: "Mégse",
      save: "Beállítások mentése",
      categories: {
        necessary: {
          title: "Szükséges",
          description: "Az oldal alapvető működéséhez és biztonságához szükséges. Mindig aktív.",
        },
        functional: {
          title: "Funkcionális",
          description: "Kiegészítő funkciók, például spamvédelem, térkép vagy videó beágyazás.",
        },
        analytics: {
          title: "Analitika",
          description: "Anonim használati mérések, például IP-anonimizált Google Analytics.",
        },
        marketing: {
          title: "Marketing",
          description: "Személyre szabott hirdetések és remarketing pixelek.",
        },
      },
    },
    privacy: {
      heading: "Adatvédelmi szabályzat",
      updated: "Utolsó frissítés: 2025-09-01",
      whoTitle: "1. Kik vagyunk",
      whoText:
        "A Fixanor Software Solutions S.R.L. egy kis szoftverfejlesztő vállalkozás Nagyváradon, Romániában.",
      dataTitle: "2. Milyen személyes adatokat gyűjtünk",
      dataItems: [
        "Az Ön neve",
        "Az Ön e-mail címe",
        "Az üzenet tárgya és tartalma",
        "Az Ön IP-címe, amelyet a Cloudflare Turnstile automatikusan gyűjt",
        "Cloudflare Turnstile ellenőrzési adatok",
      ],
      useTitle: "3. Hogyan használjuk az adatait",
      useIntro: "Személyes adatait kizárólag az alábbi célokra használjuk:",
      useItems: ["Válaszadás az üzeneteire", "Spam és visszaélések megelőzése Cloudflare Turnstile segítségével"],
      storageTitle: "4. Hogyan tároljuk és küldjük az adatait",
      storageText1:
        "Üzenete biztonságosan, saját backend szerverünkön keresztül, Brevo SMTP használatával jut el közvetlenül a Zoho Mail fiókunkba (contact@fixanor.com). Ezekhez az e-mailekhez csak arra jogosult Fixanor munkatársak férnek hozzá.",
      storageText2:
        "Üzenetet nem tárolunk adatbázisban, és adatait nem használjuk marketing vagy analitikai célokra.",
      cookiesTitle: "5. Sütik és követés",
      cookiesText:
        "Ezen a weboldalon nem használunk követő technológiákat hozzájárulás nélkül. A spamvédelemhez használt Cloudflare Turnstile ideiglenes, nem követési célú sütiket vagy hasonló adatokat helyezhet el a böngészőben a megfelelő működéshez. Ezek biztonsági célból szükségesek, és nem követik Önt más weboldalakon.",
      rightsTitle: "6. Az Ön jogai",
      rightsIntro: "Önnek joga van:",
      rightsItems: [
        "Hozzáférni személyes adataihoz",
        "Helyesbítést vagy törlést kérni",
        "Bármikor visszavonni hozzájárulását",
        "Panaszt tenni a román adatvédelmi hatóságnál (ANSPDCP)",
      ],
      rightsContact: "Jogai gyakorlásához írjon nekünk a contact@fixanor.com címre.",
      legalTitle: "7. Az adatkezelés jogalapja",
      legalText:
        "Személyes adatait az Ön kifejezett hozzájárulása alapján kezeljük, amikor elküldi a kapcsolatfelvételi űrlapot.",
      retentionTitle: "8. Adatmegőrzés",
      retentionText:
        "Az üzeneteket csak addig őrizzük meg, amíg ez szükséges a megkeresés megválaszolásához. Archiválási célból megmaradhatnak e-mail fiókunkban, hacsak Ön nem kér törlést.",
      thirdPartyTitle: "9. Harmadik fél szolgáltatásai",
      thirdPartyIntro: "Az alábbi GDPR-kompatibilis szolgáltatásokat használjuk:",
      brevo: "Brevo (SMTP e-mail küldés)",
      zoho: "Zoho Mail (EU-s e-mail fiók tárhely)",
      cloudflare: "Cloudflare (DNS-kezelés és Turnstile spamvédelem)",
      changesTitle: "10. A szabályzat módosítása",
      changesText: "Ezt az adatvédelmi szabályzatot időnként frissíthetjük. Minden változás ezen az oldalon jelenik meg.",
      contactTitle: "11. Kapcsolat",
      address:
        "Municipiul Oradea, Str. ALUMINEI, Nr. 74, Bloc D5, Apartament 48, Județ Bihor",
    },
    impressum: {
      heading: "Jogi közlemény (Impresszum)",
      updated: "Utolsó frissítés: 2025-07-24",
      companyTitle: "Cégazonosítás",
      registered: "Romániában bejegyezve",
      registrationNumber: "Cégjegyzékszám (Nr. ordine RC)",
      euid: "EU egyedi azonosító (EUID)",
      taxCode: "Adószám (CUI)",
      certificate: "Bejegyzési tanúsítvány",
      issuedOn: "kiadva: 2025-07-03",
      incorporationAct: "Alapító okirat",
      legalForm: "Jogi forma",
      legalFormValue: "Societate cu Răspundere Limitată (korlátolt felelősségű társaság)",
      registrationDate: "Bejegyzés dátuma",
      status: "Állapot",
      active: "Aktív",
      duration: "Időtartam",
      unlimited: "Határozatlan",
      headOfficeTitle: "Székhely",
      addressLabel: "Cím",
      address:
        "Municipiul Oradea, Str. Aluminei, Nr. 74, Bloc D5, Apartament 48, Județ Bihor, Románia",
      representativeTitle: "Képviselő",
      ceo: "Ügyvezető",
      responsible: "Felelős a weboldal tartalmáért és az üzleti tevékenységért.",
      vatTitle: "Áfa státusz",
      vatText1: "A Fixanor Software Solutions S.R.L. jelenleg nem áfa-regisztrált.",
      vatText2: "Minden számla áfa nélkül kerül kiállításra a vonatkozó román jogszabályok szerint.",
      copyrightTitle: "Szerzői jog és felelősség",
      copyrightText:
        "A weboldal teljes tartalma, beleértve a szövegeket, kódot és vizuális anyagokat, szerzői jogi védelem alatt áll. Jogosulatlan felhasználása, másolása vagy terjesztése tilos. Nem vállalunk felelősséget a domainről hivatkozott külső weboldalak tartalmáért.",
      odrTitle: "EU online vitarendezés",
      odrText1:
        "Az Európai Bizottság online vitarendezési (ODR) platformja 2025. július 20-án megszűnt.",
      odrText2:
        "Az EU fogyasztói panaszaira és alternatív vitarendezésére vonatkozó naprakész információkért keresse fel:",
      disclaimerTitle: "Felelősségkizárás",
      disclaimerText:
        "Ez a weboldal kizárólag szoftverfejlesztési szolgáltatásokról szóló tájékoztatási célokat szolgál. Nem minősül kereskedelmi ajánlatnak vagy kötelező érvényű szerződésnek.",
    },
    notFound: {
      heading: "404 - Az oldal nem található",
      text: "A keresett oldal nem létezik.",
    },
  },
  ro: {
    common: {
      email: "Email",
      phone: "Telefon",
      privacyPolicy: "Politica de confidențialitate",
      cookieSettings: "Setări cookie",
    },
    language: {
      label: "Limbă",
      openMenu: "Alege limba",
      select: "Schimbă limba în:",
    },
    theme: {
      toggleToDark: "Activează modul întunecat",
      toggleToLight: "Activează modul luminos",
    },
    seo: {
      defaultTitle: "Fixanor Software Solutions",
      defaultDescription: "Dezvoltare web profesională și soluții software din Oradea.",
      homeTitle: "Acasă",
      homeDescription:
        "Fixanor Software Solutions - dezvoltare web profesională, software personalizat și soluții digitale pentru creșterea afacerii online.",
      contactTitle: "Contact",
      contactDescription:
        "Contactează Fixanor Software Solutions pentru dezvoltare web profesională și soluții software personalizate.",
      privacyTitle: "Politica de confidențialitate",
      privacyDescription:
        "Află cum Fixanor Software Solutions colectează, procesează și protejează datele tale.",
      impressumTitle: "Impressum",
      impressumDescription:
        "Date oficiale ale companiei și informații legale pentru Fixanor Software Solutions S.R.L.",
      notFoundTitle: "Pagina nu a fost găsită",
      notFoundDescription:
        "Pagina căutată nu a fost găsită. Descoperă serviciile și expertiza Fixanor Software Solutions.",
    },
    hero: {
      titleLine1: "Construim viitorul",
      titleLine2: "software-ului",
      cta: "Contact",
      imageAlt: "Dezvoltator software",
    },
    about: {
      title: "Despre Fixanor",
      intro: {
        before:
          "este o companie de dezvoltare software din Oradea, România. Suntem specializați în construirea de",
        websites: "site-uri web",
        between: "și",
        webApplications: "aplicații web",
        after: " moderne, rapide și fiabile, adaptate nevoilor unice ale clienților noștri.",
      },
      philosophyPrefix: "Filosofia noastră este simplă:",
      philosophyQuote: "Soluții strălucite pentru provocări.",
      approach:
        "Combinăm creativitatea, expertiza tehnică și o abordare orientată spre client pentru a transforma ideile în soluții digitale scalabile.",
      values: [
        {
          title: "Inovație",
          description: "Folosim tehnologii moderne pentru a livra experiențe digitale de vârf.",
        },
        {
          title: "Parteneriat",
          description: "Credem în colaborare transparentă și relații pe termen lung.",
        },
        {
          title: "Simplitate",
          description: "Construim soluții prietenoase, eficiente și elegante pentru fiecare proiect.",
        },
      ],
      closing:
        "Fie că ai nevoie de un site de prezentare, un magazin online sau o aplicație web personalizată, Fixanor te ajută să reușești în lumea digitală.",
    },
    carousel: {
      title: "Tehnologiile noastre",
      logoAlt: "Logo tehnologie",
    },
    contact: {
      title: "Contact",
      placeholders: {
        subject: "Subiect",
        name: "Numele tău",
        email: "Adresa ta de e-mail",
        message: "Mesajul tău",
      },
      consentBefore: "Sunt de acord ca Fixanor să proceseze datele mele personale conform",
      consentAfter: ".",
      functionalCookiesMessage: "Pentru a trimite formularul, activează cookie-urile funcționale în",
      functionalCookiesSuffix: ".",
      turnstileNotConfiguredNotice: "Turnstile nu este configurat în acest mediu.",
      send: "Trimite",
      alerts: {
        rateLimit: "Te rugăm să aștepți cel puțin 2 minute înainte de a trimite un alt mesaj.",
        functionalCookies: "Te rugăm să activezi cookie-urile funcționale în setările cookie.",
        turnstileNotConfigured: "Turnstile nu este configurat. Te rugăm să contactezi suportul.",
        captcha: "Te rugăm să confirmi că nu ești robot.",
        privacy: "Te rugăm să accepți politica de confidențialitate pentru a continua.",
        success: "Mesajul tău a fost trimis cu succes!",
        backendNotConfigured: "URL-ul backend nu este configurat.",
        errorPrefix: "Eroare: ",
        networkErrorPrefix: "Eroare de rețea: ",
      },
    },
    footer: {
      rights: "Toate drepturile rezervate.",
      impressum: "Impressum",
    },
    cookie: {
      settings: "Setări cookie",
      bannerAria: "Banner cookie",
      bannerTextBefore:
        "Acest site folosește cookie-uri pentru funcționalitatea de bază și pentru îmbunătățirea experienței. Cookie-urile neesențiale, precum cele funcționale, analitice și de marketing, sunt folosite doar cu acordul tău. Consultă",
      bannerTextAfter: ".",
      decline: "Refuză",
      manage: "Gestionează preferințele",
      acceptAll: "Acceptă tot",
      preferencesAria: "Preferințe cookie",
      preferencesTitle: "Preferințe cookie",
      close: "Închide",
      preferencesDescription: "Alege categoriile pe care vrei să le permiți. Le poți modifica oricând.",
      cancel: "Anulează",
      save: "Salvează preferințele",
      categories: {
        necessary: {
          title: "Necesare",
          description: "Necesare pentru funcționalitatea de bază și securitatea site-ului. Sunt mereu active.",
        },
        functional: {
          title: "Funcționale",
          description: "Funcții suplimentare precum protecție anti-spam, hărți sau conținut video încorporat.",
        },
        analytics: {
          title: "Analitice",
          description: "Măsurători anonime de utilizare, de exemplu Google Analytics cu anonimizarea IP-ului.",
        },
        marketing: {
          title: "Marketing",
          description: "Reclame personalizate si pixeli de remarketing.",
        },
      },
    },
    privacy: {
      heading: "Politica de confidențialitate",
      updated: "Ultima actualizare: 2025-09-01",
      whoTitle: "1. Cine suntem",
      whoText:
        "Fixanor Software Solutions S.R.L. este o companie mică de dezvoltare software cu sediul în Oradea, România.",
      dataTitle: "2. Ce date personale colectăm",
      dataItems: [
        "Numele tău",
        "Adresa ta de e-mail",
        "Subiectul și conținutul mesajului tău",
        "Adresa ta IP, colectată automat prin Cloudflare Turnstile",
        "Date de verificare Cloudflare Turnstile",
      ],
      useTitle: "3. Cum folosim datele tale",
      useIntro: "Folosim datele tale personale doar pentru:",
      useItems: ["A răspunde mesajelor tale", "A preveni spamul și abuzurile prin Cloudflare Turnstile"],
      storageTitle: "4. Cum sunt stocate și trimise datele tale",
      storageText1:
        "Mesajul tău este trimis în siguranță prin serverul nostru backend, folosind Brevo SMTP, direct către inboxul nostru Zoho Mail (contact@fixanor.com). Doar personalul autorizat Fixanor are acces la aceste e-mailuri.",
      storageText2:
        "Nu stocăm mesajul tău într-o bază de date și nu folosim datele tale în scopuri de marketing sau analiză.",
      cookiesTitle: "5. Cookie-uri și urmărire",
      cookiesText:
        "Nu folosim tehnologii de urmărire fără acordul tău pe acest site. Cloudflare Turnstile, folosit pentru protecția anti-spam, poate plasa cookie-uri temporare, fără urmărire, sau date similare în browser pentru funcționare corectă. Acestea sunt necesare pentru securitate și nu te urmăresc pe alte site-uri.",
      rightsTitle: "6. Drepturile tale",
      rightsIntro: "Ai dreptul să:",
      rightsItems: [
        "Accesezi datele tale personale",
        "Soliciți corectarea sau ștergerea lor",
        "Îți retragi consimțământul în orice moment",
        "Depui o plângere la Autoritatea română pentru protecția datelor (ANSPDCP)",
      ],
      rightsContact: "Pentru exercitarea drepturilor tale, contactează-ne la contact@fixanor.com.",
      legalTitle: "7. Temeiul legal al prelucrării",
      legalText:
        "Prelucrăm datele tale personale pe baza consimțământului explicit atunci când trimiți formularul de contact.",
      retentionTitle: "8. Păstrarea datelor",
      retentionText:
        "Mesajele sunt păstrate doar atât timp cât este necesar pentru a răspunde solicitării tale. Ele pot rămâne în inboxul nostru pentru arhivare, cu excepția cazului în care soliciți ștergerea.",
      thirdPartyTitle: "9. Servicii terțe",
      thirdPartyIntro: "Folosim următoarele servicii conforme GDPR:",
      brevo: "Brevo (livrare e-mail prin SMTP)",
      zoho: "Zoho Mail (găzduire inbox e-mail în UE)",
      cloudflare: "Cloudflare (gestionare DNS și protecție anti-spam Turnstile)",
      changesTitle: "10. Modificări ale acestei politici",
      changesText: "Putem actualiza periodic această politică de confidențialitate. Toate modificările vor fi publicate pe această pagină.",
      contactTitle: "11. Contact",
      address:
        "Municipiul Oradea, Str. ALUMINEI, Nr. 74, Bloc D5, Apartament 48, Județ Bihor",
    },
    impressum: {
      heading: "Informații legale (Impressum)",
      updated: "Ultima actualizare: 2025-07-24",
      companyTitle: "Identificarea companiei",
      registered: "Înregistrată în România",
      registrationNumber: "Număr de ordine în Registrul Comerțului (Nr. ordine RC)",
      euid: "Identificator unic european (EUID)",
      taxCode: "Cod de identificare fiscală (CUI)",
      certificate: "Certificat de înregistrare",
      issuedOn: "emis la 2025-07-03",
      incorporationAct: "Act constitutiv",
      legalForm: "Forma juridică",
      legalFormValue: "Societate cu Răspundere Limitată",
      registrationDate: "Data înregistrării",
      status: "Stare",
      active: "Activă",
      duration: "Durata",
      unlimited: "Nelimitată",
      headOfficeTitle: "Sediul social",
      addressLabel: "Adresa",
      address:
        "Municipiul Oradea, Str. Aluminei, Nr. 74, Bloc D5, Apartament 48, Județ Bihor, România",
      representativeTitle: "Reprezentant",
      ceo: "Administrator",
      responsible: "Responsabil pentru conținutul și activitatea comercială a acestui site.",
      vatTitle: "Statut TVA",
      vatText1: "Fixanor Software Solutions S.R.L. nu este în prezent înregistrată în scopuri de TVA.",
      vatText2: "Toate facturile sunt emise fără TVA conform legislației românești aplicabile.",
      copyrightTitle: "Drepturi de autor și răspundere",
      copyrightText:
        "Tot conținutul acestui site, inclusiv texte, cod și materiale vizuale, este protejat prin drepturi de autor. Utilizarea, reproducerea sau distribuirea neautorizată este interzisă. Nu suntem responsabili pentru conținutul site-urilor externe către care există linkuri de pe acest domeniu.",
      odrTitle: "Soluționarea online a litigiilor în UE",
      odrText1:
        "Platforma de soluționare online a litigiilor (ODR) a Comisiei Europene a fost închisă la 20 iulie 2025.",
      odrText2:
        "Pentru informații actualizate despre reclamații ale consumatorilor și soluționarea alternativă a litigiilor în UE, vizitează:",
      disclaimerTitle: "Disclaimer",
      disclaimerText:
        "Acest site are exclusiv scop informativ privind serviciile de dezvoltare software. Nu reprezintă o ofertă comercială sau un contract obligatoriu.",
    },
    notFound: {
      heading: "404 - Pagina nu a fost găsită",
      text: "Pagina pe care o cauți nu există.",
    },
  },
  en: {
    common: {
      email: "Email",
      phone: "Phone",
      privacyPolicy: "Privacy Policy",
      cookieSettings: "Cookie Settings",
    },
    language: {
      label: "Language",
      openMenu: "Choose language",
      select: "Switch language to:",
    },
    theme: {
      toggleToDark: "Turn on dark mode",
      toggleToLight: "Turn on light mode",
    },
    seo: {
      defaultTitle: "Fixanor Software Solutions",
      defaultDescription: "Professional web development and software solutions from Oradea.",
      homeTitle: "Home",
      homeDescription:
        "Fixanor Software Solutions - professional web development, custom software, and digital solutions to help your business grow online.",
      contactTitle: "Contact",
      contactDescription:
        "Contact Fixanor Software Solutions today for professional web development and custom software solutions.",
      privacyTitle: "Privacy Policy",
      privacyDescription:
        "Learn how Fixanor Software Solutions collects, processes, and protects your data.",
      impressumTitle: "Impressum",
      impressumDescription:
        "Official company details and legal disclosure of Fixanor Software Solutions S.R.L.",
      notFoundTitle: "Page Not Found",
      notFoundDescription:
        "The page you are looking for cannot be found. Explore Fixanor Software Solutions and discover our services and expertise.",
    },
    hero: {
      titleLine1: "Build the Future",
      titleLine2: "of Software",
      cta: "Contact Us",
      imageAlt: "Software Developer",
    },
    about: {
      title: "About Fixanor",
      intro: {
        before:
          "is a software development company based in Oradea, Romania. We specialize in building modern, fast, and reliable",
        websites: "websites",
        between: "and",
        webApplications: "web applications",
        after: ", tailored to the unique needs of our clients.",
      },
      philosophyPrefix: "Our philosophy is simple:",
      philosophyQuote: "Brilliant solutions to challenges.",
      approach:
        "We combine creativity, technical expertise, and a customer-first approach to turn your ideas into scalable digital solutions.",
      values: [
        {
          title: "Innovation",
          description: "We embrace modern technologies to deliver cutting-edge digital experiences.",
        },
        {
          title: "Partnership",
          description: "We believe in transparent collaboration and long-term relationships.",
        },
        {
          title: "Simplicity",
          description: "We build user-friendly, efficient, and elegant solutions for every project.",
        },
      ],
      closing:
        "Whether you need a company website, an online shop, or a custom web application, Fixanor is here to help you succeed in the digital world.",
    },
    carousel: {
      title: "Our Tech Stack",
      logoAlt: "Technology logo",
    },
    contact: {
      title: "Contact Us",
      placeholders: {
        subject: "Subject",
        name: "Your name",
        email: "Your email address",
        message: "Your message",
      },
      consentBefore: "I consent to Fixanor processing my personal data in accordance with the",
      consentAfter: ".",
      functionalCookiesMessage: "To send the form, please enable Functional cookies in",
      functionalCookiesSuffix: ".",
      turnstileNotConfiguredNotice: "Turnstile is not configured on this environment.",
      send: "Send",
      alerts: {
        rateLimit: "Please wait at least 2 minutes before sending another message.",
        functionalCookies: "Please enable Functional cookies in Cookie settings.",
        turnstileNotConfigured: "Turnstile is not configured. Please contact support.",
        captcha: "Please verify that you are not a robot.",
        privacy: "Please accept the Privacy Policy to continue.",
        success: "Your message has been sent successfully!",
        backendNotConfigured: "Backend URL is not configured.",
        errorPrefix: "Error: ",
        networkErrorPrefix: "Network error: ",
      },
    },
    footer: {
      rights: "All rights reserved.",
      impressum: "Impressum",
    },
    cookie: {
      settings: "Cookie Settings",
      bannerAria: "Cookie banner",
      bannerTextBefore:
        "This website uses cookies for basic functionality and to improve your experience. Non-essential cookies, such as functional, analytics, and marketing cookies, are used only with your consent. See our",
      bannerTextAfter: ".",
      decline: "Decline",
      manage: "Manage preferences",
      acceptAll: "Accept all",
      preferencesAria: "Cookie preferences",
      preferencesTitle: "Cookie preferences",
      close: "Close",
      preferencesDescription: "Choose which categories you want to allow. You can change this anytime.",
      cancel: "Cancel",
      save: "Save preferences",
      categories: {
        necessary: {
          title: "Necessary",
          description: "Required for core site functionality and security. Always on.",
        },
        functional: {
          title: "Functional",
          description: "Enhancements like spam protection, maps, or video embeds.",
        },
        analytics: {
          title: "Analytics",
          description: "Anonymous usage metrics, such as Google Analytics with IP anonymization.",
        },
        marketing: {
          title: "Marketing",
          description: "Personalized ads and remarketing pixels.",
        },
      },
    },
    privacy: {
      heading: "Privacy Policy",
      updated: "Last updated: 2025-09-01",
      whoTitle: "1. Who We Are",
      whoText:
        "Fixanor Software Solutions S.R.L. is a small software development company based in Oradea, Romania.",
      dataTitle: "2. What Personal Data We Collect",
      dataItems: [
        "Your name",
        "Your email address",
        "The subject and content of your message",
        "Your IP address, automatically collected via Cloudflare Turnstile",
        "Cloudflare Turnstile verification data",
      ],
      useTitle: "3. How We Use Your Data",
      useIntro: "We use your personal data only to:",
      useItems: ["Respond to your messages", "Prevent spam and abuse via Cloudflare Turnstile"],
      storageTitle: "4. How Your Data Is Stored and Sent",
      storageText1:
        "Your message is securely sent via our backend server using Brevo SMTP directly to our Zoho Mail inbox (contact@fixanor.com). Only authorized Fixanor staff have access to these emails.",
      storageText2:
        "We do not store your message in any database, and we do not use your data for marketing or analytics purposes.",
      cookiesTitle: "5. Cookies and Tracking",
      cookiesText:
        "We do not use tracking technologies without your consent on this website. Cloudflare Turnstile, which we use for spam protection, may place temporary, non-tracking cookies or similar data in your browser to function properly. These are necessary for security purposes and do not track you across other websites.",
      rightsTitle: "6. Your Rights",
      rightsIntro: "You have the right to:",
      rightsItems: [
        "Access your personal data",
        "Request correction or deletion",
        "Withdraw your consent at any time",
        "File a complaint with the Romanian Data Protection Authority (ANSPDCP)",
      ],
      rightsContact: "To exercise any of your rights, please contact us at contact@fixanor.com.",
      legalTitle: "7. Legal Basis for Processing",
      legalText:
        "We process your personal data based on your explicit consent when you submit the contact form.",
      retentionTitle: "8. Data Retention",
      retentionText:
        "Messages are retained only as long as necessary to respond to your inquiry. They may remain in our email inbox for archival purposes unless you request deletion.",
      thirdPartyTitle: "9. Third-Party Services",
      thirdPartyIntro: "We use the following GDPR-compliant services:",
      brevo: "Brevo (SMTP email delivery)",
      zoho: "Zoho Mail (email inbox hosting in the EU)",
      cloudflare: "Cloudflare (DNS management and Turnstile spam protection)",
      changesTitle: "10. Changes to This Policy",
      changesText: "We may update this privacy policy periodically. All updates will be published on this page.",
      contactTitle: "11. Contact",
      address:
        "Municipiul Oradea, Str. ALUMINEI, Nr. 74, Bloc D5, Apartament 48, Judet Bihor",
    },
    impressum: {
      heading: "Legal Notice (Impressum)",
      updated: "Last updated: 2025-07-24",
      companyTitle: "Company Identification",
      registered: "Registered in Romania",
      registrationNumber: "Company Registration Number (Nr. ordine RC)",
      euid: "EU Unique Identifier (EUID)",
      taxCode: "Tax Identification Code (CUI)",
      certificate: "Registration Certificate",
      issuedOn: "issued on 2025-07-03",
      incorporationAct: "Incorporation Act",
      legalForm: "Legal form",
      legalFormValue: "Societate cu Raspundere Limitata (Limited Liability Company)",
      registrationDate: "Date of registration",
      status: "Status",
      active: "Active",
      duration: "Duration",
      unlimited: "Unlimited",
      headOfficeTitle: "Head Office",
      addressLabel: "Address",
      address:
        "Municipiul Oradea, Str. Aluminei, Nr. 74, Bloc D5, Apartament 48, Judet Bihor, Romania",
      representativeTitle: "Representative",
      ceo: "CEO",
      responsible: "Responsible for content and business activity of this website.",
      vatTitle: "VAT Status",
      vatText1: "Fixanor Software Solutions S.R.L. is currently not VAT registered.",
      vatText2: "All invoices are issued without VAT under applicable Romanian legislation.",
      copyrightTitle: "Copyright & Liability",
      copyrightText:
        "All content on this website, including text, code, and visual materials, is protected by copyright. Unauthorized use, reproduction, or distribution is prohibited. We are not responsible for the content of external websites linked from this domain.",
      odrTitle: "EU Online Dispute Resolution",
      odrText1:
        "The European Commission's Online Dispute Resolution (ODR) platform was discontinued as of 20 July 2025.",
      odrText2:
        "For up-to-date information on consumer complaints and alternative dispute resolution in the EU, please visit:",
      disclaimerTitle: "Disclaimer",
      disclaimerText:
        "This website is intended solely for informational purposes regarding software development services. It does not represent a commercial offer or binding contract.",
    },
    notFound: {
      heading: "404 - Page Not Found",
      text: "The page you are looking for does not exist.",
    },
  },
  de: {
    common: {
      email: "E-Mail",
      phone: "Telefon",
      privacyPolicy: "Datenschutzerklarung",
      cookieSettings: "Cookie-Einstellungen",
    },
    language: {
      label: "Sprache",
      openMenu: "Sprache auswahlen",
      select: "Sprache wechseln zu:",
    },
    theme: {
      toggleToDark: "Dunklen Modus aktivieren",
      toggleToLight: "Hellen Modus aktivieren",
    },
    seo: {
      defaultTitle: "Fixanor Software Solutions",
      defaultDescription: "Professionelle Webentwicklung und Softwarelosungen aus Oradea.",
      homeTitle: "Startseite",
      homeDescription:
        "Fixanor Software Solutions - professionelle Webentwicklung, individuelle Software und digitale Losungen, damit Ihr Unternehmen online wachsen kann.",
      contactTitle: "Kontakt",
      contactDescription:
        "Kontaktieren Sie Fixanor Software Solutions fur professionelle Webentwicklung und individuelle Softwarelosungen.",
      privacyTitle: "Datenschutzerklarung",
      privacyDescription:
        "Erfahren Sie, wie Fixanor Software Solutions Ihre Daten erhebt, verarbeitet und schutzt.",
      impressumTitle: "Impressum",
      impressumDescription:
        "Offizielle Unternehmensangaben und rechtliche Hinweise der Fixanor Software Solutions S.R.L.",
      notFoundTitle: "Seite nicht gefunden",
      notFoundDescription:
        "Die gesuchte Seite wurde nicht gefunden. Entdecken Sie die Leistungen und Expertise von Fixanor Software Solutions.",
    },
    hero: {
      titleLine1: "Wir bauen die Zukunft",
      titleLine2: "der Software",
      cta: "Kontakt",
      imageAlt: "Softwareentwickler",
    },
    about: {
      title: "Uber Fixanor",
      intro: {
        before:
          "ist ein Softwareentwicklungsunternehmen mit Sitz in Oradea, Rumanien. Wir entwickeln moderne, schnelle und zuverlassige",
        websites: "Websites",
        between: "und",
        webApplications: "Webanwendungen",
        after: ", abgestimmt auf die individuellen Anforderungen unserer Kunden.",
      },
      philosophyPrefix: "Unsere Philosophie ist einfach:",
      philosophyQuote: "Brillante Losungen fur Herausforderungen.",
      approach:
        "Wir verbinden Kreativitat, technisches Know-how und eine kundenorientierte Arbeitsweise, um Ihre Ideen in skalierbare digitale Losungen zu verwandeln.",
      values: [
        {
          title: "Innovation",
          description: "Wir nutzen moderne Technologien, um erstklassige digitale Erlebnisse zu liefern.",
        },
        {
          title: "Partnerschaft",
          description: "Wir glauben an transparente Zusammenarbeit und langfristige Beziehungen.",
        },
        {
          title: "Einfachheit",
          description: "Wir entwickeln benutzerfreundliche, effiziente und elegante Losungen fur jedes Projekt.",
        },
      ],
      closing:
        "Ob Unternehmenswebsite, Onlineshop oder individuelle Webanwendung: Fixanor hilft Ihnen, in der digitalen Welt erfolgreich zu sein.",
    },
    carousel: {
      title: "Unser Tech-Stack",
      logoAlt: "Technologie-Logo",
    },
    contact: {
      title: "Kontakt",
      placeholders: {
        subject: "Betreff",
        name: "Ihr Name",
        email: "Ihre E-Mail-Adresse",
        message: "Ihre Nachricht",
      },
      consentBefore: "Ich willige ein, dass Fixanor meine personenbezogenen Daten gemaß der",
      consentAfter: "verarbeitet.",
      functionalCookiesMessage: "Um das Formular zu senden, aktivieren Sie bitte funktionale Cookies in den",
      functionalCookiesSuffix: ".",
      turnstileNotConfiguredNotice: "Turnstile ist in dieser Umgebung nicht konfiguriert.",
      send: "Senden",
      alerts: {
        rateLimit: "Bitte warten Sie mindestens 2 Minuten, bevor Sie eine weitere Nachricht senden.",
        functionalCookies: "Bitte aktivieren Sie funktionale Cookies in den Cookie-Einstellungen.",
        turnstileNotConfigured: "Turnstile ist nicht konfiguriert. Bitte kontaktieren Sie den Support.",
        captcha: "Bitte bestatigen Sie, dass Sie kein Roboter sind.",
        privacy: "Bitte akzeptieren Sie die Datenschutzerklarung, um fortzufahren.",
        success: "Ihre Nachricht wurde erfolgreich gesendet!",
        backendNotConfigured: "Die Backend-URL ist nicht konfiguriert.",
        errorPrefix: "Fehler: ",
        networkErrorPrefix: "Netzwerkfehler: ",
      },
    },
    footer: {
      rights: "Alle Rechte vorbehalten.",
      impressum: "Impressum",
    },
    cookie: {
      settings: "Cookie-Einstellungen",
      bannerAria: "Cookie-Hinweis",
      bannerTextBefore:
        "Diese Website verwendet Cookies fur die Grundfunktionen und zur Verbesserung Ihrer Erfahrung. Nicht notwendige Cookies, etwa funktionale, Analyse- und Marketing-Cookies, werden nur mit Ihrer Einwilligung verwendet. Lesen Sie unsere",
      bannerTextAfter: ".",
      decline: "Ablehnen",
      manage: "Einstellungen verwalten",
      acceptAll: "Alle akzeptieren",
      preferencesAria: "Cookie-Einstellungen",
      preferencesTitle: "Cookie-Einstellungen",
      close: "Schließen",
      preferencesDescription: "Wahlen Sie aus, welche Kategorien Sie erlauben mochten. Sie konnen dies jederzeit andern.",
      cancel: "Abbrechen",
      save: "Einstellungen speichern",
      categories: {
        necessary: {
          title: "Notwendig",
          description: "Erforderlich fur Grundfunktionen und Sicherheit der Website. Immer aktiv.",
        },
        functional: {
          title: "Funktional",
          description: "Erweiterungen wie Spam-Schutz, Karten oder eingebettete Videos.",
        },
        analytics: {
          title: "Analyse",
          description: "Anonyme Nutzungsmetriken, etwa Google Analytics mit IP-Anonymisierung.",
        },
        marketing: {
          title: "Marketing",
          description: "Personalisierte Anzeigen und Remarketing-Pixel.",
        },
      },
    },
    privacy: {
      heading: "Datenschutzerklarung",
      updated: "Zuletzt aktualisiert: 2025-09-01",
      whoTitle: "1. Wer wir sind",
      whoText:
        "Fixanor Software Solutions S.R.L. ist ein kleines Softwareentwicklungsunternehmen mit Sitz in Oradea, Rumanien.",
      dataTitle: "2. Welche personenbezogenen Daten wir erheben",
      dataItems: [
        "Ihr Name",
        "Ihre E-Mail-Adresse",
        "Betreff und Inhalt Ihrer Nachricht",
        "Ihre IP-Adresse, automatisch uber Cloudflare Turnstile erhoben",
        "Cloudflare Turnstile-Verifizierungsdaten",
      ],
      useTitle: "3. Wie wir Ihre Daten verwenden",
      useIntro: "Wir verwenden Ihre personenbezogenen Daten ausschließlich, um:",
      useItems: ["Auf Ihre Nachrichten zu antworten", "Spam und Missbrauch uber Cloudflare Turnstile zu verhindern"],
      storageTitle: "4. Wie Ihre Daten gespeichert und gesendet werden",
      storageText1:
        "Ihre Nachricht wird sicher uber unseren Backend-Server mit Brevo SMTP direkt an unser Zoho Mail-Postfach (contact@fixanor.com) gesendet. Nur autorisierte Fixanor-Mitarbeiter haben Zugriff auf diese E-Mails.",
      storageText2:
        "Wir speichern Ihre Nachricht in keiner Datenbank und verwenden Ihre Daten nicht fur Marketing- oder Analysezwecke.",
      cookiesTitle: "5. Cookies und Tracking",
      cookiesText:
        "Wir verwenden auf dieser Website keine Tracking-Technologien ohne Ihre Einwilligung. Cloudflare Turnstile, das wir zum Spam-Schutz einsetzen, kann vorubergehende, nicht trackingbezogene Cookies oder ahnliche Daten in Ihrem Browser speichern, damit es ordnungsgemaß funktioniert. Diese sind aus Sicherheitsgrunden notwendig und verfolgen Sie nicht uber andere Websites hinweg.",
      rightsTitle: "6. Ihre Rechte",
      rightsIntro: "Sie haben das Recht:",
      rightsItems: [
        "Auf Ihre personenbezogenen Daten zuzugreifen",
        "Berichtigung oder Loschung zu verlangen",
        "Ihre Einwilligung jederzeit zu widerrufen",
        "Eine Beschwerde bei der rumanischen Datenschutzbehorde (ANSPDCP) einzureichen",
      ],
      rightsContact: "Zur Ausubung Ihrer Rechte kontaktieren Sie uns bitte unter contact@fixanor.com.",
      legalTitle: "7. Rechtsgrundlage der Verarbeitung",
      legalText:
        "Wir verarbeiten Ihre personenbezogenen Daten auf Grundlage Ihrer ausdrucklichen Einwilligung, wenn Sie das Kontaktformular absenden.",
      retentionTitle: "8. Aufbewahrung der Daten",
      retentionText:
        "Nachrichten werden nur so lange aufbewahrt, wie es zur Beantwortung Ihrer Anfrage erforderlich ist. Sie konnen zu Archivzwecken in unserem E-Mail-Postfach verbleiben, sofern Sie keine Loschung verlangen.",
      thirdPartyTitle: "9. Dienste Dritter",
      thirdPartyIntro: "Wir verwenden die folgenden DSGVO-konformen Dienste:",
      brevo: "Brevo (SMTP-E-Mail-Zustellung)",
      zoho: "Zoho Mail (E-Mail-Postfach-Hosting in der EU)",
      cloudflare: "Cloudflare (DNS-Verwaltung und Turnstile-Spam-Schutz)",
      changesTitle: "10. Anderungen dieser Richtlinie",
      changesText: "Wir konnen diese Datenschutzerklarung gelegentlich aktualisieren. Alle Anderungen werden auf dieser Seite veroffentlicht.",
      contactTitle: "11. Kontakt",
      address:
        "Municipiul Oradea, Str. ALUMINEI, Nr. 74, Bloc D5, Apartament 48, Judet Bihor",
    },
    impressum: {
      heading: "Rechtlicher Hinweis (Impressum)",
      updated: "Zuletzt aktualisiert: 2025-07-24",
      companyTitle: "Unternehmensangaben",
      registered: "In Rumanien registriert",
      registrationNumber: "Handelsregisternummer (Nr. ordine RC)",
      euid: "EU-Eindeutige Kennung (EUID)",
      taxCode: "Steueridentifikationsnummer (CUI)",
      certificate: "Registrierungszertifikat",
      issuedOn: "ausgestellt am 2025-07-03",
      incorporationAct: "Grundungsurkunde",
      legalForm: "Rechtsform",
      legalFormValue: "Societate cu Raspundere Limitata (Gesellschaft mit beschrankter Haftung)",
      registrationDate: "Registrierungsdatum",
      status: "Status",
      active: "Aktiv",
      duration: "Dauer",
      unlimited: "Unbegrenzt",
      headOfficeTitle: "Sitz",
      addressLabel: "Adresse",
      address:
        "Municipiul Oradea, Str. Aluminei, Nr. 74, Bloc D5, Apartament 48, Judet Bihor, Rumanien",
      representativeTitle: "Vertretung",
      ceo: "Geschaftsfuhrer",
      responsible: "Verantwortlich fur Inhalt und geschaftliche Tatigkeit dieser Website.",
      vatTitle: "Umsatzsteuerstatus",
      vatText1: "Fixanor Software Solutions S.R.L. ist derzeit nicht fur Umsatzsteuer registriert.",
      vatText2: "Alle Rechnungen werden gemaß geltendem rumanischem Recht ohne Umsatzsteuer ausgestellt.",
      copyrightTitle: "Urheberrecht und Haftung",
      copyrightText:
        "Alle Inhalte dieser Website, einschließlich Texte, Code und visuelle Materialien, sind urheberrechtlich geschutzt. Unbefugte Nutzung, Vervielfaltigung oder Verbreitung ist untersagt. Wir sind nicht fur die Inhalte externer Websites verantwortlich, die von dieser Domain verlinkt werden.",
      odrTitle: "EU-Online-Streitbeilegung",
      odrText1:
        "Die Online-Streitbeilegungsplattform (ODR) der Europaischen Kommission wurde zum 20. Juli 2025 eingestellt.",
      odrText2:
        "Aktuelle Informationen zu Verbraucherbeschwerden und alternativer Streitbeilegung in der EU finden Sie unter:",
      disclaimerTitle: "Haftungsausschluss",
      disclaimerText:
        "Diese Website dient ausschließlich Informationszwecken zu Softwareentwicklungsleistungen. Sie stellt kein kommerzielles Angebot und keinen verbindlichen Vertrag dar.",
    },
    notFound: {
      heading: "404 - Seite nicht gefunden",
      text: "Die gesuchte Seite existiert nicht.",
    },
  },
};

const LanguageContext = createContext(null);

const getNestedValue = (source, key) =>
  key.split(".").reduce((current, part) => {
    if (current === undefined || current === null) {
      return undefined;
    }

    return current[part];
  }, source);

const getLanguageFromUrl = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const urlLanguage = new URLSearchParams(window.location.search).get(LANGUAGE_QUERY_PARAM);
  return isSupportedLanguage(urlLanguage) ? urlLanguage : null;
};

const replaceUrlLanguage = (language) => {
  if (typeof window === "undefined" || !isSupportedLanguage(language)) {
    return;
  }

  const url = new URL(window.location.href);
  if (url.searchParams.get(LANGUAGE_QUERY_PARAM) === language) {
    return;
  }

  url.searchParams.set(LANGUAGE_QUERY_PARAM, language);
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
};

const getInitialLanguage = () => {
  if (typeof window === "undefined") {
    return "en";
  }

  const urlLanguage = getLanguageFromUrl();
  if (urlLanguage) {
    return urlLanguage;
  }

  try {
    const savedLanguage = window.localStorage.getItem(STORAGE_KEY);
    if (isSupportedLanguage(savedLanguage)) {
      return savedLanguage;
    }
  } catch {}

  const browserLanguage = window.navigator?.language?.slice(0, 2).toLowerCase();
  return isSupportedLanguage(browserLanguage) ? browserLanguage : "en";
};

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, language);
    } catch {}

    document.documentElement.lang = language;
    replaceUrlLanguage(language);
  }, [language]);

  useEffect(() => {
    const syncLanguageFromUrl = () => {
      const urlLanguage = getLanguageFromUrl();
      if (urlLanguage) {
        setLanguageState(urlLanguage);
      }
    };

    window.addEventListener("popstate", syncLanguageFromUrl);
    return () => window.removeEventListener("popstate", syncLanguageFromUrl);
  }, []);

  const value = useMemo(() => {
    const setLanguage = (nextLanguage) => {
      if (isSupportedLanguage(nextLanguage)) {
        setLanguageState(nextLanguage);
      }
    };

    const withLanguage = (to, nextLanguage = language) => {
      const url = new URL(to, window.location.origin);
      url.searchParams.set(LANGUAGE_QUERY_PARAM, nextLanguage);
      return `${url.pathname}${url.search}${url.hash}`;
    };

    const t = (key) => {
      const translatedValue = getNestedValue(translations[language], key);
      if (translatedValue !== undefined) {
        return translatedValue;
      }

      const fallbackValue = getNestedValue(translations.en, key);
      return fallbackValue !== undefined ? fallbackValue : key;
    };

    return {
      language,
      currentLanguage: LANGUAGE_OPTIONS.find((option) => option.code === language) || LANGUAGE_OPTIONS[2],
      languages: LANGUAGE_OPTIONS,
      setLanguage,
      withLanguage,
      t,
    };
  }, [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used inside LanguageProvider");
  }

  return context;
}
